import type { Step } from '@lucid/lego';
import type { VehicleOrder } from '../../../api/services/lupp/order/importer/dto.importer';

const VEHICLE_ORDER_STATUS_STEPS = [
  'Planning Production',
  'Planned Production',
  'In Production',
  'Production Complete',
  'In Transit to US Port',
  'At US Port',
  'In Transit',
  'Vehicle at Port',
  'In Transit to Delivery Center',
  'Vehicle Ready for Pickup',
  'Delivered',
];

/** Step labels that were renamed, still accepted by {@link resolveStepIndex}. */
const LEGACY_STEP_LABELS: Record<string, number> = {
  'vehicle ready for dispatch': 9,
};

/**
 * Maps order-platform `vehicleJourneyStatus` enum values to their index in
 * {@link VEHICLE_ORDER_STATUS_STEPS}.
 */
const VEHICLE_JOURNEY_STATUS_INDEX: Record<string, number> = {
  PLANNING_PRODUCTION: 0,
  PLANNED_PRODUCTION: 1,
  IN_PRODUCTION: 2,
  PRODUCTION_COMPLETE: 3,
  IN_TRANSIT_TO_US_PORT: 4,
  AT_US_PORT: 5,
  IN_TRANSIT: 6,
  VEHICLE_AT_PORT: 7,
  IN_TRANSIT_TO_DELIVERY_CENTER: 8,
  READY_FOR_DISPATCH: 9,
  DELIVERED: 10,
};

const resolveStepIndex = (currentStatus: string | null | undefined): number => {
  if (!currentStatus) return 0;

  const enumIndex = VEHICLE_JOURNEY_STATUS_INDEX[currentStatus.toUpperCase()];
  if (enumIndex !== undefined) return enumIndex;

  const labelIndex = VEHICLE_ORDER_STATUS_STEPS.findIndex(
    (s) => s.toLowerCase() === currentStatus.toLowerCase()
  );
  if (labelIndex !== -1) return labelIndex;

  return LEGACY_STEP_LABELS[currentStatus.toLowerCase()] ?? 0;
};

export const buildStepperSteps = (currentStatus: string | null | undefined): Step[] => {
  const resolvedIndex = resolveStepIndex(currentStatus);

  return VEHICLE_ORDER_STATUS_STEPS.map((stepKey, index) => {
    let status: Step['status'];
    if (index < resolvedIndex) status = 'completed';
    else if (index === resolvedIndex) status = 'active';
    else status = 'pending';
    return { id: `step-${index}`, label: stepKey, status };
  });
};

const ORDER_CONFIRMATION_STATUSES = new Set([
  'CONFIRMED',
  'READY FOR DISPATCH',
  'FULFILLED',
  'DELIVERED',
]);

export const isOrderConfirmationAvailable = (order: VehicleOrder): boolean => {
  const vin = order.vin ?? order.vehicle?.vin;
  const status = order.statusName?.replace(/_/g, ' ').toUpperCase() ?? '';
  return Boolean(vin?.trim()) && ORDER_CONFIRMATION_STATUSES.has(status);
};
